import React, {useContext, useEffect} from 'react';
import {Typography} from '@mui/material';
import {Box} from '@mui/system';
import {
  AutoFieldHelpers,
  useAutoField,
  useAutoForm,
  useLoadedAutoField,
} from '../../../components/AutoForm/useAutoForm';
import {
  RecommendProps,
  getValidation,
  ValidationObjectType,
} from '../CreateRecommend/RecommendSourceDestination/validators';
import SourceDestination from '../CreateRecommend/RecommendSourceDestination/SourceDestination';
import {recommendFieldsType} from '../CreateRecommend/recommendFields';
import {
  annuityGuaranteePeriodField,
  annuityIncomeFrequencyField,
  annuityIndexLinkedField,
  annuityLongTermCareField,
  annuityLtcProtectedPercentField,
  annuityNotesField,
  annuityPaymentTypeField,
  annuityRateTypeField,
  annuitySpousePercentField,
  annuityTypeField,
  setUpAnnuityFields,
} from './annuityFields';
import {
  AnnuityIncomeFrequencyEnum,
  AnnuityPaymentTypeEnum,
  AnnuityRateTypeEnum,
  AnnuityTypeEnum,
} from '../../../schema-types/globalTypes';
import {
  transferFieldsType,
  transferSourceType,
} from '../RecommendsTransfer/transferFields';
import {POTS_PENSION} from '../../../utils/potsFilter';
import {useRecommendContext} from '../CreateRecommend/CreateRecommendHelpers';
import RecommendPlanForm, {
  RecommendPlanFormsStyled,
} from '../CreateRecommend/RecommendSourceDestination/RecommendPlanForm';
import {getSummary} from '../CreateRecommend/RecommendSourceDestination/naming';
import {TransferSourceForm} from '../RecommendsTransfer/TransferSourceForm';
import {getCurrencyFloat} from '../../../utils/currencyFormatter';
import {AccountContext} from '../../../contexts/AccountContext';
import {Pot} from '../../../schema-types/Pot';
import {OVRecommendation} from '../../../schema-types/OVRecommendation';
import {optionalRound} from '../CreateRecommend/RecommendSourceDestination/utils';
import {nullableBooleanToYesNoEmpty} from '../RecommendsTransfer/nullableBooleanToYesNoEmpty';
import {getFeeFormikFromServer} from '../CreateRecommend/DAInitialFees';
import {defaultFeeFields, getPlanFeesFormikFromServer} from '../CreateRecommend/PlanFees';
import AutoField from '../../../components/AutoForm/AutoFieldNew';
import {UPDATES} from '../../../shared/UPDATES';

interface loadAnnuityProps {
  recommendDetails: OVRecommendation | undefined | null;
  annuityHelpers: AutoFieldHelpers<setUpAnnuityFields | undefined>;
  transferSourcesFieldHelpers: AutoFieldHelpers<
    Record<string, transferSourceType> | undefined
  >;
  isSPPDA: boolean;
}

export const loadAnnuity = ({
  recommendDetails,
  annuityHelpers,
  transferSourcesFieldHelpers,
  isSPPDA,
}: loadAnnuityProps) => {
  if (!recommendDetails) {
    const annuityValues: setUpAnnuityFields = {
      annuityType: AnnuityTypeEnum.PERSONAL_PENSION_ANNUITY,
      incomeFrequency: AnnuityIncomeFrequencyEnum.MONTHLY,
      rateType: AnnuityRateTypeEnum.ORDINARY_RATES,
      paymentType: AnnuityPaymentTypeEnum.IN_ARREARS,
    };

    annuityHelpers.setValue(UPDATES._700, annuityValues);
    return;
  }

  const destinationPot = recommendDetails?.pots?.find(
    (p) => p?.planType === 'destination'
  );

  const sourcePot = recommendDetails?.pots?.find((p) => p?.planType === 'source');
  const sourceId = sourcePot?.pot?.id;

  const annuityValues: setUpAnnuityFields = {
    annuityType: destinationPot?.annuityType ?? undefined,
    incomeFrequency: destinationPot?.incomeFrequency ?? undefined,
    guaranteePeriod: destinationPot?.guaranteedPeriod ?? undefined,
    spouseAnnuityPercent: destinationPot?.spouseAnnuityPercent ?? undefined,
    rateType: destinationPot?.rateType ?? undefined,
    indexLinked: destinationPot?.indexLinked ?? undefined,
    longTermCareCapitalProtected: destinationPot?.ltcCapitalProtected ?? undefined,
    ltcProtectedPercent: destinationPot?.protectedPercentage ?? undefined,
    sourceId,
    paymentType: destinationPot?.paymentType ?? undefined,
    notes: recommendDetails.note ?? undefined,
  };

  annuityHelpers.setValue(UPDATES._701, annuityValues);

  const sourceValues: Record<string, transferSourceType> = {
    [sourceId]: {
      id: sourceId,
      singleClientAmount: optionalRound(sourcePot?.singleClientAmount),
      full: sourcePot?.full || false,
      isAuthorityHeld: nullableBooleanToYesNoEmpty(sourcePot?.pot?.isAuthorityHeld),
      isDataGather: nullableBooleanToYesNoEmpty(sourcePot?.pot?.isDataGather),
      maxAmount: sourcePot?.pot?.totalValue,
      contactId: sourcePot?.pot?.contact?.id,
      checked: true,
      isShellPlan: false,
      planValue: sourcePot?.pot?.currentDirectValue,
      inSpecie: sourcePot?.inSpecie ?? sourcePot?.pot?.inSpecie ?? undefined,
      initialFee: sourcePot?.initialFee?.id,
      legacyInitialFee: getFeeFormikFromServer(isSPPDA, sourcePot?.legacyInitialFee),
      planFees: getPlanFeesFormikFromServer(sourcePot) || defaultFeeFields,
    },
  };

  transferSourcesFieldHelpers.setValue(UPDATES._703, sourceValues);
};

export const AnnuityPlan: React.FC<RecommendProps> = ({
  sourcePots,
  destinationPots,
  sourceDestinationLabels,
  contactName,
}) => {
  const {pots} = useContext(AccountContext);
  const {values, setFieldValue} = useAutoForm<recommendFieldsType>();
  const {planAction, mainPlanType} = values;
  const {recommendDetails, loadingOvRecommend, recommendReviewId, isSPPDA} =
    useRecommendContext();
  const {loaded, setLoaded} = useLoadedAutoField();
  const [annuityField, annuityMeta, annuityHelpers] = useAutoField<
    setUpAnnuityFields | undefined
  >('annuity');

  const changeSource = (event: React.ChangeEvent<any> | null, value: string) => {
    const id = value || event?.target?.value;
    annuityHelpers.setValue(UPDATES._702, {
      ...annuityField.value,
      sourceId: id,
    });
  };
  const [transferSourcesFieldObj, transferSourcesFieldMeta, transferSourcesFieldHelpers] =
    useAutoField<transferFieldsType['transferSources'] | undefined>(
      'transfer.transferSources'
    );
  const createValidation = (el: Pot) =>
    Object.keys(transferSourcesFieldMeta.value || {}).includes(el.id)
      ? {
          totalFields: Object.keys(transferSourcesFieldMeta.value?.[el.id] || {}).length,
          invalidFields: Object.keys(transferSourcesFieldMeta.error?.[el.id] || {})
            .length,
        }
      : undefined;

  useEffect(() => {
    setFieldValue(UPDATES._704, 'transfer', {transferSources: {}})
      .then(() => {
        loadAnnuity({
          recommendDetails,
          annuityHelpers,
          transferSourcesFieldHelpers,
          isSPPDA,
        });
      })
      .finally(() => {
        setLoaded();
      });
  }, [recommendDetails]);

  const filteredSourcePots = pots.filter(
    (p) => p.productName?.acronym?.toLocaleLowerCase() === POTS_PENSION && !p.parent
  );

  return (
    <>
      <SourceDestination
        fromError={transferSourcesFieldMeta.error}
        toError={annuityMeta.error}
        source={
          <RecommendPlanFormsStyled>
            {filteredSourcePots.map((el, i) => (
              <RecommendPlanForm
                key={el.id}
                id={el.id}
                reccId={el.originalOvrId}
                summary={getSummary(el)}
                details={
                  <TransferSourceForm
                    pot={el}
                    id={el?.id}
                    index={i}
                    maxAmount={getCurrencyFloat(el?.totalValue)}
                    hasInSpecie={false}
                  />
                }
                curoId={el.curoId}
                validation={createValidation(el)}
                isSelected={annuityField.value?.sourceId === el.id}
                isSubplan={false}
                handleChange={changeSource}
              />
            ))}
          </RecommendPlanFormsStyled>
        }
        destination={<Annuity loaded={loaded} validation={getValidation(annuityMeta)} />}
        sourceLabel={sourceDestinationLabels?.first}
        destinationLabel={sourceDestinationLabels?.second}
      />
    </>
  );
};

const Annuity: React.FC<{loaded?: boolean; validation: ValidationObjectType}> = ({
  loaded,
  validation,
}) => {
  const [longTermCareField, longTermCareMeta, longTermCareHelpers] =
    useAutoField<boolean>('annuity.longTermCareCapitalProtected');

  if (!loaded) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <RecommendPlanForm
      reccId=""
      validation={validation}
      summary={
        <Typography variant="subtitle1" gutterBottom>
          Annuity Details
        </Typography>
      }
      details={
        <Box sx={{'& > *': {mb: '1rem !important'}}}>
          <AutoField field={annuityTypeField} />
          <AutoField field={annuityPaymentTypeField} />
          <AutoField field={annuityIncomeFrequencyField} />
          <AutoField field={annuityGuaranteePeriodField} />
          <AutoField field={annuitySpousePercentField} />
          <AutoField field={annuityRateTypeField} />
          <AutoField field={annuityIndexLinkedField} />
          <AutoField field={annuityLongTermCareField} />
          {longTermCareField.value && (
            <AutoField field={annuityLtcProtectedPercentField} />
          )}
          <AutoField field={annuityNotesField} />
        </Box>
      }
      isSelected
      isSubplan={false}
      handleChange={() => {}}
    />
  );
};
